import IconButton from '@/global/components/Button/IconButton'
import { FooterAviso } from '@/global/components/DataGrid/components/DataTable/Footer'
import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import useDefaultFilterModel from '@/global/components/DataGrid/hooks/useDefaultFilterModel'
import { GridColDefPro } from '@/global/components/DataGrid/utils/types'
import { hasPermission } from '@/hooks/useBackendMenu'
import { NotFound } from '@/templates/NotFound'
import { CategoriaEnum } from '@acme/auth'
import { CircularProgress } from '@mui/material'
import dayjs from 'dayjs'
import { Button } from 'global/components/Button'
import SelectCompetencia, {
    useCompetencia,
} from 'global/components/Selects/SelectCompetencia'
import ContentGuide from 'global/guides/ContentGuide'
import { useAuth } from 'hooks'
import { DownloadIcon, Trash2Icon } from 'lucide-react'
import { useState } from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'
import { ajustes, TImpressos } from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'

export default function RelatoriosAjustesImpressos() {
    const [isAssinando, setIsAssinando] = useState(false)
    const [competencia, setCompetencia] = useCompetencia()
    const empresaId = useStore(state => state.companyId)
    const [filterModel, setFilterModel] = useDefaultFilterModel()
    const {
        funcionarioSelecionado,
        decoded: { categoria },
    } = useAuth()
    const { data = [], isLoading } = ajustes.impressos.useFetch({
        queryParams: {
            empresaId,
            competencia,
            funcionarioId:
                categoria === 5 ? funcionarioSelecionado?.id : undefined,
        },
        enabled:
            empresaId > 0 &&
            (categoria === 5 ? !!funcionarioSelecionado : true),
    })
    const { mutateAsync } = ajustes.impressos.mutations.useUpdate({
        onSuccess: () => {
            ajustes.impressos.invalidateQueries()
            toast.success('Relatório assinado com sucesso!')
        },
        onError: err => {
            toast.error(err.message)
        },
        queryParams: {
            funcionarioId: funcionarioSelecionado?.id,
        },
    })

    async function handleAssinar({ hash }: any) {
        setIsAssinando(true)

        return mutateAsync({
            id: hash,
            funcionarioId: funcionarioSelecionado?.id,
        } as any).finally(() => setIsAssinando(false))
    }

    // Módulo não habilitado
    if (!hasPermission(['read', 'AssinaturaEletrônicaDeAjustes']))
        return <NotFound />

    return (
        <ContentGuide>
            <ContentGuide.Card>
                <ContentGuide.Header
                    label={[
                        'Relatórios',
                        {
                            title: 'Ajustes',
                            to: '/relatorios/ajustes',
                        },
                        'Impressos',
                    ]}
                >
                    <ContentGuide.Filtros SelectEmpresas>
                        <SelectCompetencia
                            onChange={setCompetencia}
                            value={competencia}
                        />
                    </ContentGuide.Filtros>
                </ContentGuide.Header>
                <ContentGuide.DataGrid
                    rows={data}
                    loading={isLoading}
                    columns={[
                        {
                            field: 'createdAt',
                            headerName: 'Data de geração',
                            width: 150,
                            type: 'date',
                            valueFormatter: ({ value }) =>
                                dayjs(value).format('DD/MM/YYYY HH:mm'),
                        },
                        {
                            field: 'Funcionario',
                            headerName: 'Funcionário',
                            width: 350,
                            valueGetter: ({ value }) => value?.nome,
                        },
                        {
                            field: 'arquivo',
                            headerName: 'Arquivo',
                            flex: 1,
                        },
                        categoria === CategoriaEnum.Funcionario
                            ? {
                                  ...optionsColumn,
                                  field: 'assinadoEm',
                                  width: 120,
                                  renderCell: ({ value, row }) =>
                                      value ? (
                                          <Button disabled>Assinado</Button>
                                      ) : (
                                          <Button
                                              onClick={() => handleAssinar(row)}
                                              disabled={isAssinando}
                                              startIcon={
                                                  isAssinando ? (
                                                      <CircularProgress
                                                          size={16}
                                                      />
                                                  ) : undefined
                                              }
                                          >
                                              Assinar
                                          </Button>
                                      ),
                              }
                            : {
                                  field: 'assinadoEm',
                                  headerName: 'Data de assinatura',
                                  width: 180,
                                  type: 'date',
                                  valueFormatter: ({ value }) =>
                                      value
                                          ? dayjs(value).format(
                                                'DD/MM/YYYY HH:mm',
                                            )
                                          : '',
                                  renderCell: ({ value }) =>
                                      value ? (
                                          <span
                                              className="flex items-center"
                                              title={value}
                                          >
                                              {dayjs(value).format(
                                                  'DD/MM/YYYY HH:mm',
                                              )}
                                          </span>
                                      ) : (
                                          <span className="flex items-center">
                                              Não assinado
                                          </span>
                                      ),
                              },
                        {
                            ...optionsColumn,
                            field: '__download',
                            renderCell: ({ row: { hash } }) => (
                                <IconButton
                                    title="Baixar arquivo"
                                    onClick={() => {
                                        window.open(
                                            `${
                                                import.meta.env.VITE_BASE_URL
                                            }/v1/ajustes/pdf/${hash}`,
                                        )
                                    }}
                                    disabled={isAssinando}
                                >
                                    <DownloadIcon />
                                </IconButton>
                            ),
                        },
                        ...(hasPermission([
                            'delete',
                            'AssinaturaEletrônicaDeAjustes',
                        ])
                            ? [
                                  {
                                      ...optionsColumn,
                                      field: '__delete',
                                      renderCell: ({ row: { hash } }) => (
                                          <IconButton
                                              title="Excluir relatório"
                                              href={`${hash}/excluir`}
                                          >
                                              <Trash2Icon className="text-red-500" />
                                          </IconButton>
                                      ),
                                  } as GridColDefPro<TImpressos>,
                              ]
                            : []),
                    ]}
                    filterModel={filterModel}
                    onFilterModelChange={setFilterModel}
                    ToolbarProps={{
                        exportButtonProps: {
                            fileName: 'exportacao-ajustes-realizados',
                        },
                    }}
                    components={{
                        Footer: () => (
                            <FooterAviso severity="info">
                                Estes são os relatórios de ajustes já impressos
                                que estão disponíveis para aceite digital.
                            </FooterAviso>
                        ),
                    }}
                />
            </ContentGuide.Card>
            <Outlet context={{ data }} />
        </ContentGuide>
    )
}

export function useRelatoriosAjustesImpressosOutletContext() {
    return useOutletContext<{
        data: TImpressos[]
    }>()
}
